import type { TicketDetail } from '@nightshift/ui-types';

import { useTimeFormat } from '../../lib/display.ts';
import { observedTime } from '../../lib/format.ts';

/**
 * Header panel for a ticket with unmet blockers.
 *
 * It sits beside the Waiting panel rather than replacing it: a ticket parked for a
 * clarification can also depend on something, and hiding either fact behind the other is how
 * an operator ends up answering the question and then wondering why nothing moved.
 *
 * Deliberately not a call to action. The panel states the fact; the Dependencies tab is where
 * anything gets changed.
 */
export function DependencyPanel({
  detail,
  now,
  onOpenTab,
}: {
  detail: TicketDetail;
  now: number;
  onOpenTab: () => void;
}) {
  const format = useTimeFormat();
  const unmet = detail.dependencies.dependsOn.filter((edge) => edge.chip !== 'satisfied');
  if (unmet.length === 0) return null;
  const cancelled = unmet.filter((edge) => edge.chip === 'unavailable');

  return (
    <section
      className={`drawer-depblock${cancelled.length > 0 ? ' is-unavailable' : ''}`}
      aria-label="Dependencies"
    >
      <div className="drawer-depblock-head">
        <span className="drawer-depblock-kicker">
          Depends on {unmet.length === 1 ? '1 ticket' : `${unmet.length} tickets`}
        </span>
        <button type="button" className="btn btn-sm" onClick={onOpenTab}>
          Manage
        </button>
      </div>
      <ul className="drawer-depblock-list">
        {unmet.map((edge) => (
          <li key={edge.ticketKey}>
            <span className="mono">{edge.ticketKey}</span>
            {' · '}
            {edge.state}
            {edge.chip === 'unavailable' ? ' · cancelled, so this will never clear' : ''}
            {edge.chip === 'stale' ? ' · not moved in a fortnight' : ''}
            {' · '}
            {observedTime(edge.since, now, format)}
          </li>
        ))}
      </ul>
      <p className="drawer-depblock-meta">
        Start refuses while these are unfinished. Start anyway records that you did.
      </p>
    </section>
  );
}
